<template>
    <v-app :class="{['v-application--' + platform]: true,}">
        <loading :loading="!appLoadedOnce || loading"></loading>
        <force-update v-if="appLoadedOnce" />
        
        <template v-if="appLoadedOnce">
            <navbar v-if="!$route.meta.landingFullScreen" :height="navbarBaseHeight" @menu="openMenu = !openMenu"/>
            
            <app-menu v-if="!$route.meta.landingFullScreen" v-model="openMenu"/>

            <!-- confirm email msg -->
            <div v-if="innerApp && !confirmed" class="fixed-top-bar d-flex align-center justify-space-between justify-md-center pa-0 white--text orange" :style="`top: ${effectiveNavbarHeight}px`">
                <div class="px-3">لم يتم تأكيد البريد الإلكتروني</div>
                <v-btn text large tile color="grey darken-3" class="light--text" @click.stop="openConfirmPage">
                    <span class="white--text">تأكيد</span>
                </v-btn>
            </div>
            
            <announcement-dialog v-if="appData && innerApp && !announcementDismissed && appData.general_announcement" :value="appData && appData.general_announcement" />
            
            <!-- full screen overlay - app is in maintainance -->
            <app-down :show="appIsDown" /> 

            <v-main v-if="appLoadedOnce && !appIsDown" class="main-container" :class="{'pc': pcOnly, 'white': phoneOnly || $route.meta.whiteBackground, [$route.name + '-page']: true, 'landing-fullscreen': $route.meta.landingFullScreen}" :style="mainPaddingStyle">
                <transition :name="transitionName" mode="out-in">
                    <router-view>
                        <!-- to add footer, just put <slot name="footer"> in view and set route meta.footer to true then footer will be auto inserted & transition will be auto changed   -->
                        <e-footer slot="footer" :fixed="!$route.meta.footer.notFixed" v-if="$route.meta.footer && $route.meta.footer.show" />
                    </router-view>
                </transition> 
            </v-main>

            <bottom-navigation />

            <!-- footer -->
            <!-- <e-footer light v-if="appLoadedOnce && showFooter" /> -->
        </template>

        <v-snackbar :value="supportChatState.unread > 0 && !supportChatState.visible" :timeout="-1" bottom color="primary" role="status" aria-live="polite">
            <span>لديك رد جديد من الدعم ({{ supportChatState.unread }})</span>
            <template v-slot:action>
                <v-btn text @click="openSupportChat">فتح المحادثة</v-btn>
            </template>
        </v-snackbar>
        <app-msg :navbar-height="effectiveNavbarHeight" />
    </v-app>
</template>

<script>
import { ensureAgentplaneWidget, openAgentplaneChat, supportChatState } from './services/agentplane-support';
import AppDown from "./components/default/dialogs/app-down";
import AppMsg from "./components/default/app-msg";
import Loading from "./components/default/loading";
import AnnouncementDialog from "./components/default/dialogs/announcement-dialog.vue";

import GlobalComputed from './helpers/global-computed'
import GlobalMethods from './helpers/global-methods'

import Navbar from './components/main/navbar/navbar'
import BottomNavigation from './components/main/bottom-navigation'
import AppMenu from './components/main/app-menu'
import EFooter from './components/main/e-footer'

import AppAPI from '@/app-api'
 import ForceUpdate from "./components/default/dialogs/force-update";

export default {
    name: "App",
    
    components: {
        AppDown,
        Loading,
        EFooter,
        AppMsg,
        Navbar,
        AppMenu,
        BottomNavigation,
        ForceUpdate,
        AnnouncementDialog
    },

    data: () => ({
        openMenu: false,
        transitionName: 'none',
    }),
    computed: {
        supportChatState() { return supportChatState; },
        appIsDown(){
            return this.appData && this.appData.appIsDown;
        },
        showFooter(){
            return this.$route.meta.footer;
        },
        innerApp(){
            return this.isAuthed && this.$route.name !== 'confirm';
        },
        confirmed(){
            if (!this.user || !this.appData.email_verification){
                return true;
            }
            return !!this.user.email_verified_at;
        },
        loading(){
            return this.$store.state.globalModule.loading;
        },
        announcementDismissed(){
            return this.$store.state.globalModule.announcementDismissed;
        },
        mainPaddingStyle(){
            if (this.$route.meta.landingFullScreen) {
                return 'padding-top: 0';
            }
            return 'padding-top: ' + this.effectiveNavbarHeight + 'px';
        },
        ...GlobalComputed
    },
    watch: {
        $route(to, from){
            // force email verification
            this.forceEmailConfirm(to.name);
            this.forceCompleteProfile(to.name);
            this.transitionName = 'fade-up'; /* fade-up */

            if (to.meta.landingFullScreen) {
                this.openMenu = false;
            }
            
            if (to.meta.footer){
                // to fix footer bug with transition
                this.transitionName = 'none';
            }
        },
        isAuthed(authed){
            const guestRoute = this.$route.matched.some((record) => record.meta.requiresGuest);
            if (!authed && !guestRoute){
                this.$router.push({name: 'login'});
            }
        },
    },
    methods: {
        openConfirmPage(){
            this.$router.push({name: 'confirm'});
        },
        openSupportChat(){
            ensureAgentplaneWidget(this.$store)
                .then(() => openAgentplaneChat())
                .catch(() => this.$router.push({ name: 'help' }));
        },
        bootSupportChat(){
            ensureAgentplaneWidget(this.$store).catch(() => {});
        },
        forceEmailConfirm(route=null){
            if (!route){
                route = this.$route.name;
            }
            const force = process.env.VUE_APP_FORCE_CONFIRMATION;
            if (force){
                if (!this.confirmed){
                    if (route !== 'confirm'){
                        this.openConfirmPage();
                    }
                }
            }
        },
        forceCompleteProfile(route=null){
            if (!route){
                route = this.$route.name
            }
            if (this.user && this.user.profile_not_completed && route !== 'profile'){
                this.$router.push({name: 'profile'});
                window.setTimeout(()=>{
                    this.showMsg('يتعين عليك إكمال بيانات حسابك حتى تتمكن من استعمال التطبيق', 'warning', false, false, 4000);
                }, 250);
            }
        },
        afterAuthActions(){
            this.forceEmailConfirm();
            this.forceCompleteProfile();
        },
        initApp(){
            const self = this;
            // check auth and retrieve user resource
            this.$store.dispatch('checkAuth').then(response=>{

                // then set loaded to true
                window.setTimeout(()=>{
                    self.$store.dispatch('appLoaded'); // set app loaded status
                }, 250);
                self.afterAuthActions();

            }).catch(error=>{
                // check if error is 401, so it means unauthenticated otherwise show an error message and don't start app.
                const unauthenticated = this.checkErrorStatus(error, false);
                if (unauthenticated || !error){
                    // 401 response means unauthenticated
                    /* 
                    *   !error means that rejected but no error, this happens only when there's no token
                    *   so request isn't sent and our state-store returned an empty error
                    */
                    if (unauthenticated && !this.$route.matched.some((record) => record.meta.requiresGuest)){
                        this.$router.push({name: 'login'});
                    }
                    window.setTimeout(()=>{
                        self.$store.dispatch('appLoaded'); // set app loaded status
                    }, 250);
                }else{
                    // unknown error: request faild
                    this.showMsg('(2) تعذر التحقق من بياناتك، أعد فتح التطبيق', 'error', false, false, 100000);
                }
            });

        },

        checkErrorStatus(error, reload=true){
            if (error && error.response && error.response.status){
                // if unauthorized (maybe logout or token changed)
                if (error.response.status === 401){
                    this.forceLogout(reload);
                    return true;
                }
            }
            return false;
        },
        setGlobalErrorHandler(){
            const self = this;
            window.axios.interceptors.response.use(function(response) {
                // Do something with response data
                return response;

            }, function (error) {
                // Do something with response error
                if (self.isAuthed){
                    // if user is authed but API returned 401, then force logout!
                    self.checkErrorStatus(error);
                }
                return Promise.reject(error);
            });
        },
        ...GlobalMethods,
    },
    created(){
        if (this.capRunning && this.platform === 'android') {
            const self = this;
            window.__onAndroidSdkInjected = function() {
                self.$forceUpdate();
            };
            if (window.__ANDROID_SDK_INT__ != null) {
                this.$nextTick(function() {
                    self.$forceUpdate();
                });
            }
        }
        if (this.capRunning){
            AppAPI.init(this);
            const primary = process.env.VUE_APP_STATUS_BAR_PRIMARY_COLOR === true || process.env.VUE_APP_STATUS_BAR_PRIMARY_COLOR === 'true';
            AppAPI.changeStatusbar(false, primary);
        }
    },
    beforeDestroy(){
        if (this.platform === 'android' && window.__onAndroidSdkInjected) {
            window.__onAndroidSdkInjected = null;
        }
    },
    mounted(){
        this.setGlobalErrorHandler();
        // first load app global data / settings (admin app data)
        this.$store.dispatch('loadAppData').then(()=>{
                
            // if nor running, init app now
            // we are on browser/web app!
            this.initApp();
            // Floating Agentplane bubble (inline panel still mounts on /help).
            this.bootSupportChat();

            if (this.capRunning){
                const primary = process.env.VUE_APP_STATUS_BAR_PRIMARY_COLOR === true || process.env.VUE_APP_STATUS_BAR_PRIMARY_COLOR === 'true';
                AppAPI.changeStatusbar(false, primary);
            }
            
        }).catch(()=>{
            this.showMsg('(1) تعذر تحميل بيانات التطبيق، أعد فتح التطبيق');
        })
    }
};
</script>

<style lang="scss">
@import 'scss/main';
.main-container{
    &.pc{
        // background-color: #f9f9f9;
    }

    &.login-page, &.register-page, .reset-password-page{
        @media(max-width: 960px){
            background-color: #fff!important;
        }
    }
}
</style>